import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';

/**
 * Business Card Component
 * 
 * A reusable card component for displaying business information
 * 
 * @param {Object} props Component props
 * @param {string} props.logo - Path to the business logo
 * @param {string} props.imagePath - Path to the business image in Firebase storage
 * @param {string} props.name - Business name
 * @param {string} props.description - Short description of the business (e.g., "Sweets and coffee")
 * @param {string} props.address - Business address (e.g., "23 Main street, Flushing")
 * @param {string} props.businessId - ID of the business for linking to detail page
 * @param {string} props.category - Business category
 */
const BusinessCard = ({ 
  logo, 
  imagePath, 
  name, 
  description = '', 
  address = '', 
  businessId = '',
  category = '' 
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Fetch the image URL from Firebase Storage if we have a path
  useEffect(() => {
    const fetchImage = async () => {
      if (imagePath) {
        try {
          console.log(`Fetching business image from path: ${imagePath}`);
          setImageLoading(true);
          const imageRef = ref(storage, imagePath);
          const url = await getDownloadURL(imageRef);
          console.log(`Successfully fetched image URL for business card: ${url}`);
          setImageUrl(url);
          setImageError(false);
        } catch (error) {
          console.error(`Error fetching image from path ${imagePath}:`, error);
          setImageError(true);
        } finally {
          setImageLoading(false);
        }
      } else if (logo) {
        // If we have a direct URL, use it
        setImageUrl(logo);
        setImageLoading(false);
      } else {
        setImageLoading(false);
        setImageError(true);
      }
    };

    fetchImage();
  }, [imagePath, logo]);

  // Generate initials for fallback (up to 2 letters)
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
    
  // Handle click on the card to open detail page in new tab
  const handleCardClick = (e) => {
    // Don't trigger for child links or buttons that have their own handlers
    if (e.target.closest('a') || e.target.closest('button')) {
      return;
    }
    
    if (businessId) {
      // Open business detail page in a new tab
      window.open(`/dashboard/businesses/${businessId}`, '_blank');
    }
  };

  return (
    <div 
      className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 group border border-gray-100 flex flex-col h-full max-w-[280px] cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Image Section */}
      <div className="relative h-40">
        {/* Category Badge */}
        {category && (
          <span className="absolute top-2 right-2 bg-white/90 text-[#FF8C00] text-xs font-medium px-2 py-0.5 rounded-full z-10">
            {category}
          </span>
        )}
        
        {/* Business Image */}
        {imageLoading ? (
          <div className="animate-pulse bg-gray-200 w-full h-full"></div>
        ) : !imageError && imageUrl ? (
          <img
            src={imageUrl}
            alt={`${name}`}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FF8C00] to-[#FF6B00]">
            <span className="text-white text-4xl font-bold">{initials}</span>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-3 flex-grow flex flex-col">
        {/* Business Name */}
        <h3 className="text-base font-bold text-gray-800 mb-2 group-hover:text-[#FF8C00] transition-colors truncate text-left">
          {name}
        </h3>

        {/* Description */}
        {description && (
          <p className="text-gray-600 mb-3 line-clamp-2 text-xs text-left">
            {description}
          </p>
        )}

        {/* Address with icon */}
        {address && (
          <div className="flex items-center text-gray-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                 className="h-4 w-4 mr-1.5 stroke-2 flex-shrink-0">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            <span className="truncate text-left text-xs">{address}</span>
          </div>
        )}

        {/* View Business button */}
        {businessId ? (
          <a 
            href={`/dashboard/businesses/${businessId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto block w-full bg-[#FF8C00] py-2 rounded-md text-center text-white font-medium hover:bg-[#FF8C00]/90 transition-all shadow-sm hover:shadow transform hover:-translate-y-0.5 text-sm"
            onClick={(e) => e.stopPropagation()}
          >
            View Business
          </a>
        ) : (
          <div className="relative mt-auto">
            <button 
              disabled
              className="w-full bg-gray-200 py-2 rounded-md text-center text-gray-400 font-medium cursor-not-allowed text-sm"
            >
              Browse Jobs
            </button>
            <span className="absolute -top-2 right-0 bg-white/80 text-gray-500 text-[10px] px-2 py-0.5 rounded-full inline-block italic">
              coming soon
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessCard;
